const imageFileNameCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

export function compareImageFileNames(firstName, secondName) {
  const getStemAndSuffix = (fileName) => {
    const stem = String(fileName || '').replace(/\.[^.]+$/, '');
    const match = stem.match(/^(.*?)(?:[\s._-]+([a-z]))?$/i);
    return { stem: match?.[1] || stem, suffix: match?.[2]?.toUpperCase() || '' };
  };
  const first = getStemAndSuffix(firstName);
  const second = getStemAndSuffix(secondName);
  if (!first.suffix && second.suffix) return -1;
  if (first.suffix && !second.suffix) return 1;
  const suffixOrder = imageFileNameCollator.compare(first.suffix, second.suffix);
  if (suffixOrder !== 0) return suffixOrder;
  const stemOrder = imageFileNameCollator.compare(first.stem, second.stem);
  return stemOrder || imageFileNameCollator.compare(firstName, secondName);
}

/**
 * Resizes an image (provided as a dataURL) so that neither dimension exceeds
 * the given maxWidth / maxHeight, preserving aspect ratio.
 * Output is encoded as image/webp for better compression.
 *
 * @param {string} dataUrl  - Base-64 dataURL of the source image
 * @param {number} maxWidth  - Maximum allowed width in pixels
 * @param {number} maxHeight - Maximum allowed height in pixels
 * @returns {Promise<string>} - Resolved with the (possibly resized) dataURL
 */
export function resizeImage(dataUrl, maxWidth, maxHeight) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;

      if (width <= maxWidth && height <= maxHeight) {
        resolve(dataUrl);
        return;
      }

      const scale = Math.min(maxWidth / width, maxHeight / height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/webp', 0.92));
    };
    img.onerror = () => {
      console.error('[resizeImage] Failed to load image for resizing; using original.');
      resolve(dataUrl);
    };
    img.src = dataUrl;
  });
}
